// PounceAppointmentsPage.jsx
// ─────────────────────────────────────────────────────────────────────────────
// Pounce ki appointments ka page (/pounce-appointments). Yahan aate hain:
//   • Dashboard ke Pounce card ka "Booked Appts" button
//   • Pounce agent page ka "Booking Appts" tile
//   • Pounce footer ka "Appointments" tab
//
// Upar logo + date range, phir Upcoming / Completed / Cancelled tabs, phir har
// appointment ka card. "View details" → /pounce-appointments/:id.
//
// Data: GET /pounce/appointments (dekho staticAppointments.js).
// ─────────────────────────────────────────────────────────────────────────────
import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import OutboundCallerBrand from "../OutboundCallerBrand/OutboundCallerBrand";
import { useLocation, useNavigate } from "react-router-dom";
import styles from "./PounceAppointmentsPage.module.css";
import PounceFooterNav from "../PounceAgentDetail/PounceFooterNav/PounceFooterNav";
import { fetchPounceAppointments } from "./staticAppointments";
import {
  addDays,
  formatDay,
  formatRange,
  formatTime,
  getBadge,
  initials,
  startOfWeek,
} from "./appointmentHelpers";
import {
  AlertIcon,
  BackIcon,
  BADGE_ICONS,
  CalendarIcon,
  ChevronIcon,
  ClockIcon,
  TYPE_ICONS,
} from "./AppointmentIcons";

const TABS = [
  { key: "upcoming", label: "Upcoming" },
  { key: "completed", label: "Completed" },
  { key: "cancelled", label: "Cancelled" },
];

// Date range ke presets. `weeks` = is hafte se kitne hafte aage/peeche.
const RANGES = [
  { key: "all", label: "All dates" },
  { key: "this", label: "This week", weeks: 0 },
  { key: "next", label: "Next week", weeks: 1 },
  { key: "last", label: "Last week", weeks: -1 },
];

const EMPTY_TEXT = {
  upcoming: "Appointments Outbound Caller books will show up here.",
  completed: "Finished appointments will show up here.",
  cancelled: "Cancelled appointments will show up here.",
};

const rangeBounds = (range) => {
  if (range.weeks === undefined) return null;
  const from = addDays(startOfWeek(new Date()), range.weeks * 7);
  return { from, to: addDays(from, 6) };
};

const AppointmentCard = ({ appt, onOpen }) => {
  const badge = getBadge(appt);
  const BadgeIcon = BADGE_ICONS[badge.icon];
  const TypeIcon = TYPE_ICONS[appt.type] || TYPE_ICONS.phone;
  const lead = appt.lead || {};

  return (
    <li className={styles.card}>
      <div className={styles.cardHead}>
        {lead.avatar ? (
          <img className={styles.avatar} src={lead.avatar} alt="" />
        ) : (
          <span className={styles.avatar} aria-hidden="true">
            {initials(lead.name) || "#"}
          </span>
        )}
        <span className={styles.who}>
          <span className={styles.name}>{lead.name || lead.phone}</span>
          {lead.source && <span className={styles.source}>{lead.source}</span>}
        </span>
        <span className={`${styles.badge} ${styles[badge.tone]}`}>
          <BadgeIcon size={15} />
          {badge.label}
        </span>
      </div>

      <div className={styles.when}>
        <span className={styles.whenItem}>
          <span className={styles.whenIcon}>
            <CalendarIcon />
          </span>
          <span className={styles.whenMain}>{formatDay(appt.startTime, appt.timeZone)}</span>
        </span>
        <span className={styles.whenItem}>
          <span className={styles.whenIcon}>
            <ClockIcon />
          </span>
          <span className={styles.whenText}>
            <span className={styles.whenMain}>{formatTime(appt.startTime, appt.timeZone)}</span>
            <span className={styles.whenSub}>{appt.durationMin} min</span>
          </span>
        </span>
      </div>

      <div className={styles.meeting}>
        <span className={styles.typeIcon}>
          <TypeIcon size={20} />
        </span>
        <span className={styles.meetingTitle}>{appt.title}</span>
        <button type="button" className={styles.detailsBtn} onClick={() => onOpen(appt)}>
          View details
          <ChevronIcon />
        </button>
      </div>

      {appt.provider && (
        <div className={styles.provider}>
          <img src="/svg/google_calendar.svg" alt="" width="18" height="18" />
          {appt.provider}
        </div>
      )}

      {appt.status === "cancelled" && appt.cancelReason && (
        <div className={styles.reason}>
          <AlertIcon />
          <span className={styles.reasonText}>
            <strong>Reason</strong>
            {appt.cancelReason}
          </span>
        </div>
      )}
    </li>
  );
};

const PounceAppointmentsPage = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [appointments, setAppointments] = useState(null); // null = pehli baar load ho raha hai
  const [failed, setFailed] = useState(false);
  const [activeTab, setActiveTab] = useState("upcoming");
  const [rangeKey, setRangeKey] = useState("all");
  const [rangeOpen, setRangeOpen] = useState(false);
  const rangeRef = useRef(null);

  // Footer ke "Home" ke liye: jis agent se user yahan aaya.
  const agentId =
    sessionStorage.getItem("SelectAgentId") || localStorage.getItem("SelectAgentId") || "";

  const load = useCallback(() => {
    setFailed(false);
    fetchPounceAppointments(agentId)
      .then(setAppointments)
      // 403 = team member ke paas appointments ki permission nahi (server tay karta hai).
      .catch((err) => setFailed(err?.response?.status === 403 ? "forbidden" : true));
  }, [agentId]);

  useEffect(() => {
    load();
  }, [load]);

  // Range menu ke bahar tap → band.
  useEffect(() => {
    if (!rangeOpen) return undefined;
    const close = (e) => {
      if (!rangeRef.current?.contains(e.target)) setRangeOpen(false);
    };
    document.addEventListener("pointerdown", close);
    return () => document.removeEventListener("pointerdown", close);
  }, [rangeOpen]);

  const range = RANGES.find((r) => r.key === rangeKey);
  const bounds = rangeBounds(range);

  const inRange = useMemo(() => {
    const rows = appointments || [];
    if (!bounds) return rows;
    const from = bounds.from.getTime();
    const to = addDays(bounds.to, 1).getTime();
    return rows.filter((a) => {
      const t = new Date(a.startTime).getTime();
      return t >= from && t < to;
    });
    // bounds har render naya object hai — rangeKey hi uska asli source hai.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [appointments, rangeKey]);

  const visible = inRange
    .filter((a) => a.status === activeTab)
    .sort((a, b) => new Date(a.startTime) - new Date(b.startTime));

  // Pichhli screen; seedha URL se aaye ho (history khaali) to Pounce agent ka home.
  const goBack = () =>
    location.key === "default"
      ? navigate("/fast-agent-detail", { replace: true, state: { agentId, isPounce: true } })
      : navigate(-1);

  const openDetails = (appt) =>
    navigate(`/pounce-appointments/${encodeURIComponent(appt.id)}`, { state: { appointment: appt } });

  return (
    <div className={styles.page}>
      <div className={styles.stickyTop}>
        <div className={styles.topBar}>
          {/* Back + logo — Leads page ke header jaisa. */}
          <div className={styles.brand}>
            <button type="button" className={styles.backBtn} onClick={goBack} aria-label="Back">
              <BackIcon />
            </button>
            <OutboundCallerBrand />
          </div>

          <div className={styles.range} ref={rangeRef}>
            <button
              type="button"
              className={styles.rangeBtn}
              onClick={() => setRangeOpen((v) => !v)}
              aria-haspopup="listbox"
              aria-expanded={rangeOpen}
            >
              <CalendarIcon size={16} />
              <span>{bounds ? formatRange(bounds.from, bounds.to) : range.label}</span>
              <ChevronIcon size={14} down />
            </button>
            {rangeOpen && (
              <ul className={styles.rangeMenu} role="listbox" aria-label="Date range">
                {RANGES.map((r) => (
                  <li key={r.key} role="option" aria-selected={r.key === rangeKey}>
                    <button
                      type="button"
                      className={`${styles.rangeOption} ${r.key === rangeKey ? styles.rangeOptionActive : ""}`}
                      onClick={() => {
                        setRangeKey(r.key);
                        setRangeOpen(false);
                      }}
                    >
                      {r.label}
                    </button>
                  </li>
                ))}
              </ul>
            )}
          </div>
        </div>

        <h1 className={styles.title}>Appointments</h1>
        <p className={styles.subtitle}>Manage and view all your scheduled appointments.</p>

        <div className={styles.tabs} role="tablist" aria-label="Appointment status">
          {TABS.map((tab) => {
            const selected = tab.key === activeTab;
            return (
              <button
                key={tab.key}
                type="button"
                role="tab"
                aria-selected={selected}
                className={`${styles.tab} ${selected ? styles.tabActive : ""}`}
                onClick={() => setActiveTab(tab.key)}
              >
                {tab.label}
                {/* Ginti sirf chune hue tab pe — design jaisa. */}
                {selected && appointments && ` (${visible.length})`}
              </button>
            );
          })}
        </div>
      </div>

      {appointments === null ? (
        failed ? (
          <div className={styles.state}>
            <p className={styles.stateTitle}>
              {failed === "forbidden"
                ? "You don't have access to appointments"
                : "We couldn't load your appointments"}
            </p>
            {failed === "forbidden" && (
              <p className={styles.stateText}>Ask the account owner to give you appointment access.</p>
            )}
            <button type="button" className={styles.retryBtn} onClick={load}>
              Try again
            </button>
          </div>
        ) : (
          <div className={styles.list} aria-busy="true" aria-label="Loading appointments">
            {[0, 1, 2].map((i) => (
              <div key={i} className={`${styles.card} ${styles.skeleton}`} />
            ))}
          </div>
        )
      ) : visible.length === 0 ? (
        <div className={styles.state}>
          <p className={styles.stateTitle}>No {activeTab} appointments</p>
          <p className={styles.stateText}>
            {bounds ? "Nothing in this date range. Try another range." : EMPTY_TEXT[activeTab]}
          </p>
        </div>
      ) : (
        <ul className={styles.list}>
          {visible.map((appt) => (
            <AppointmentCard key={appt.id} appt={appt} onOpen={openDetails} />
          ))}
        </ul>
      )}

      <PounceFooterNav agentId={agentId} active="appointments" />
    </div>
  );
};

export default PounceAppointmentsPage;
